import { useEffect } from 'react';

/** Set a red notification dot on the browser tab favicon when `unread > 0`.
 *  Uses a canvas to draw the base favicon SVG then overlay a dot, sets the
 *  result as a data: URL on the page's existing <link rel="icon">. */
export function useFaviconBadge(unread: number): void {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;

    // Cache the original href so we can restore it when unread drops to 0.
    const original = (link as HTMLLinkElement & { dataset: DOMStringMap }).dataset.origHref
      ?? link.href;
    if (!link.dataset.origHref) link.dataset.origHref = original;

    if (unread <= 0) {
      link.href = original;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      ctx.clearRect(0, 0, 64, 64);
      ctx.drawImage(img, 0, 0, 64, 64);
      // Badge circle (top-right) — red with white inner ring for visibility
      // against both light and dark favicons.
      ctx.beginPath();
      ctx.arc(48, 16, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();

      try {
        link.href = canvas.toDataURL('image/png');
      } catch {
        // Canvas taint (rare — SVGs loaded cross-origin). Leave original.
      }
    };
    img.onerror = () => {
      // Couldn't load the base favicon — leave it alone.
    };
    img.src = original;

    return () => {
      cancelled = true;
    };
  }, [unread]);
}
